import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { calculateInvoiceTotals } from '../../utils/invoiceCalculations';
import type { PaymentStatus } from '@dutchinvoice/types';
import { FileText, ChevronRight, Calendar, Building, Trash2, Check, Pen, Copy, Clock, Repeat2, Bell, CheckCircle } from 'lucide-react';
import { toLocalizedFormat, isFutureDate } from '@dutchinvoice/shared-utils';
import { useTranslation } from '../../hooks/useTranslation';
import { PaymentStatusSelector } from '../PaymentStatus';
import { useMoneyDisplay } from '../../utils/money';
import { useSavedInvoices, useInvoiceHasContent } from '../../stores/selectors';
import { StatusBadge } from './StatusBadge';
import { useScheduleStore, useScheduleForInvoice } from '../../stores/scheduleStore';

interface InvoiceOverviewCardProps {
  invoice: ReturnType<typeof useSavedInvoices>[number];
  onLoad: () => void;
  onDelete: (e: React.MouseEvent) => void;
  onCopy: (e: React.MouseEvent) => void;
  onUpdatePaymentStatus: (status: PaymentStatus) => void;
  isCurrent: boolean;
  isPlanned?: boolean;
}

export const InvoiceOverviewCard: React.FC<InvoiceOverviewCardProps> = React.memo(({
  invoice,
  onLoad,
  onDelete,
  onCopy,
  onUpdatePaymentStatus,
  isCurrent,
  isPlanned = false
}) => {
  const { t } = useTranslation();
  const language = useInvoiceStore((state) => state.settings.language);
  const getNextInvoiceNumber = useInvoiceStore((state) => state.getNextInvoiceNumber);
  const setEditingSection = useInvoiceStore((state) => state.setEditingSection);
  const updateReminderSent = useInvoiceStore((state) => state.updateReminderSent);
  const { formatCurrency } = useMoneyDisplay();
  const { fetchScheduleForInvoice } = useScheduleStore();
  const hasContent = useInvoiceHasContent();
  const [isSendingReminder, setIsSendingReminder] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);

  // Check if invoice is overdue
  const isOverdue = invoice.paymentStatus === 'overdue' || invoice.paymentStatus === 'overdue_reminded';

  // Get schedule from the shared cache
  const invoiceSchedule = useScheduleForInvoice(invoice.id);
  
  // Fetch schedule data if this invoice has a scheduleId
  useEffect(() => {
    const fetchSchedule = async () => {
      if (invoice.id && invoice.scheduleId) {
        try {
          await fetchScheduleForInvoice(invoice.id);
        } catch (error) {
          console.error(`Error fetching schedule for invoice ${invoice.id}:`, error);
        }
      }
    };

    fetchSchedule();
  }, [invoice.id, invoice.scheduleId, fetchScheduleForInvoice]);

  const invoiceTotal = React.useMemo(() => {
    const { total } = calculateInvoiceTotals(invoice.items, invoice.discount);
    return total;
  }, [invoice.items, invoice.discount]);

  const isDraft = !invoice.invoiceNumber;
  // Only show next number for the current working draft (no invoice number)
  const displayNumber = isDraft && isCurrent ? getNextInvoiceNumber() : invoice.invoiceNumber;

  // Format the date using our utility function
  const formattedDate = React.useMemo(() => {
    return toLocalizedFormat(invoice.date, language);
  }, [invoice.date, language]);

  // Format the next invoice date if available
  const formattedNextInvoiceDate = React.useMemo(() => {
    if (isPlanned && invoiceSchedule?.nextInvoiceDate) {
      return toLocalizedFormat(invoiceSchedule.nextInvoiceDate, language);
    }
    return null;
  }, [isPlanned, invoiceSchedule?.nextInvoiceDate, language]);

  // Determine if this is a scheduled invoice (has scheduleId)
  const isScheduledInvoice = !!invoice.scheduleId;
  
  // Determine if this is a future invoice (date is in the future)
  const isFutureInvoice = isFutureDate(invoice.date);

  // Get the schedule type for display
  const scheduleType = invoiceSchedule?.type || 'monthly';

  const handleClick = (e: React.MouseEvent) => {
    // Prevent clicking the card when clicking on interactive elements
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    
    // Don't allow clicking on planned invoices
    if (isPlanned) {
      return;
    }
    
    onLoad();
  };

  const handleScheduleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    onLoad(); // Load this invoice first
    setEditingSection({ type: 'schedule' }); // Then open schedule dialog
  };

  // Handle trash icon click for planned invoices
  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (isPlanned) {
      // For planned invoices, load the invoice and open the schedule editor
      onLoad(); // Load this invoice first
      setEditingSection({ type: 'schedule' }); // Then open schedule dialog
    } else {
      // For regular invoices, use the normal delete function
      onDelete(e);
    }
  };

  const handleReminderClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!invoice.id || !invoice.recipient?.email) {
      console.error('Cannot send reminder: missing invoice ID or recipient email');
      return;
    }

    // If a reminder was already sent before, show confirmation dialog
    if (invoice.lastReminderSentAt) {
      const lastSentDate = new Date(invoice.lastReminderSentAt).toLocaleString();
      const confirmed = window.confirm(
        `This invoice was already reminded on ${lastSentDate}.\n\nAre you sure you want to send another reminder?`
      );

      if (!confirmed) {
        return;
      }
    }

    setIsSendingReminder(true);
    try {
      const { invoiceApi } = await import('../../lib/api');
      await invoiceApi.sendInvoiceReminder(
        invoice.id,
        invoice.recipient.email,
        undefined, // customMessage - use default
        language
      );

      // Backend updated the invoice, now update local state using store action
      // Follow the same pattern as onUpdatePaymentStatus
      const now = Date.now();
      updateReminderSent(invoice.invoiceNumber, now);

      // Show success toast (would need toast system, for now console log)
      console.log(t('email.reminderSuccess.description', { email: invoice.recipient.email }));
    } catch (error: any) {
      console.error('Failed to send reminder:', error);

      // Handle 24-hour cooldown error
      if (error?.response?.data?.details?.nextAllowedAt) {
        const nextAllowed = new Date(error.response.data.details.nextAllowedAt);
        console.warn(`Reminder cooldown: can send next at ${nextAllowed.toLocaleString()}`);
      }
      // Show error toast
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Calculate if reminder button should be disabled (24-hour cooldown)
  const { isInCooldown, timeUntilNext } = React.useMemo(() => {
    if (!invoice.lastReminderSentAt) return { isInCooldown: false, timeUntilNext: null };

    const now = Date.now();
    const timeSinceLastReminder = now - invoice.lastReminderSentAt;
    const twentyFourHours = 24 * 60 * 60 * 1000;

    const inCooldown = timeSinceLastReminder < twentyFourHours;
    const timeLeft = inCooldown ? twentyFourHours - timeSinceLastReminder : 0;

    const hours = Math.floor(timeLeft / (60 * 60 * 1000));
    const minutes = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));

    return {
      isInCooldown: inCooldown,
      timeUntilNext: inCooldown ? { hours, minutes } : null
    };
  }, [invoice.lastReminderSentAt]);

  return (
    <motion.div
      key={invoice.invoiceNumber || 'draft'}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={`relative group bg-white rounded-2xl shadow-sm border transition-all duration-300 ease-out p-4 sm:p-6 
        hover:shadow-md ${isPlanned ? 'border-dashed border-primary-500 cursor-default' : 'border-gray-200 hover:border-primary-500/20 cursor-pointer'} ${
        isCurrent 
          ? isDraft 
            ? 'border-primary-500/30 bg-gradient-to-br from-primary-50/40 to-primary-100/20 hover:from-primary-50/50 hover:to-primary-100/30' 
            : 'border-primary-500/30 bg-primary-50/30 hover:bg-primary-50/40'
          : ''
      }`}
      onClick={handleClick}
    >
      {/* Mobile layout */}
      <div className="sm:hidden">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start space-x-2">
            <div className={`w-8 h-8 xs:w-10 xs:h-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
              isCurrent 
                ? isDraft
                  ? 'bg-gradient-to-br from-primary-100 to-primary-50 ring-2 xs:ring-4 ring-primary-100/50 animate-pulse-subtle'
                  : 'bg-primary-100 ring-2 xs:ring-4 ring-primary-50'
                : 'bg-gray-50 group-hover:bg-primary-50 group-hover:ring-2 xs:group-hover:ring-4 group-hover:ring-primary-50/50'
            }`}>
              {isCurrent ? (
                isDraft ? (
                  <Pen className="w-3 h-3 xs:w-4 xs:h-4 text-primary-600 transition-all duration-300 group-hover:scale-110" />
                ) : (
                  <Check className="w-4 h-4 xs:w-5 xs:h-5 text-primary-600" />
                )
              ) : (
                <FileText className="w-4 h-4 xs:w-5 xs:h-5 text-gray-400 group-hover:text-primary-600 transition-colors duration-200" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm xs:text-base font-medium text-gray-900 truncate mb-0.5">
                {isDraft ? t('invoice.workingDraft') : t('invoice.invoiceFor', { company: invoice.recipient.company })}
                {isScheduledInvoice && (
                  <button
                    onClick={handleScheduleClick}
                    className="ml-1 inline-flex items-center gap-1 xs:gap-1.5 px-2 xs:px-3 py-0.5 xs:py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors duration-200"
                  >
                    <Repeat2 className="w-2.5 h-2.5 xs:w-3 xs:h-3" />
                    <span className="hidden xs:inline">{t(`schedule.${scheduleType}`)}</span>
                  </button>
                )}
              </h3>
              <div className="flex items-center">
                <Building className="w-3 h-3 xs:w-3.5 xs:h-3.5 mr-1 text-gray-400" />
                <span className="truncate max-w-[120px] xs:max-w-[150px] text-xs xs:text-sm text-gray-500">{invoice.sender.company}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg xs:text-xl font-medium text-gray-900">
              {formatCurrency(invoiceTotal)}
            </p>
            {!isPlanned && (
              <p className={`text-xs xs:text-sm font-medium ${isDraft ? 'text-primary-500' : 'text-gray-500'}`}>
                {displayNumber}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 xs:pt-3 border-t">
          <div className="flex items-center gap-1 xs:gap-2 flex-wrap">
            {!isPlanned && <StatusBadge status={invoice.status} />}
            {!isPlanned && (
              <PaymentStatusSelector
                status={invoice.paymentStatus}
                onStatusChange={(status) => onUpdatePaymentStatus(status)}
              />
            )}
            <div className="flex items-center ml-0.5 xs:ml-1">
              {isPlanned ? (
                formattedNextInvoiceDate ? (
                  <>
                    <Clock className="w-3 h-3 xs:w-3.5 xs:h-3.5 mr-0.5 xs:mr-1 text-primary-500" />
                    <span className="text-xs text-primary-600 font-medium">{formattedNextInvoiceDate}</span>
                  </>
                ) : null
              ) : (
                <>
                  <Calendar className="w-3 h-3 xs:w-3.5 xs:h-3.5 mr-0.5 xs:mr-1 text-gray-500" />
                  <span className="text-xs text-gray-500">{formattedDate}</span>
                </>
              )}
            </div>
            {!isPlanned && formattedNextInvoiceDate && (
              <div className="flex items-center ml-2 xs:ml-3">
                <Clock className="w-3 h-3 xs:w-3.5 xs:h-3.5 mr-0.5 xs:mr-1 text-primary-500" />
                <span className="text-xs text-primary-600 font-medium">{formattedNextInvoiceDate}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-0.5 xs:space-x-1">
            {!isPlanned && (
              <button
                onClick={onCopy}
                className={`p-1.5 xs:p-2 text-gray-400 hover:text-primary-600 rounded-full hover:bg-primary-50 transition-colors duration-200 ${
                  isDraft ? 'invisible' : ''
                }`}
                aria-label={t('actions.copy')}
              >
                <Copy className="w-4 h-4 xs:w-5 xs:h-5" />
              </button>
            )}
            {!isPlanned && !isDraft && isOverdue && (
              <div className="relative group/reminder">
                <button
                  onClick={handleReminderClick}
                  disabled={isSendingReminder || isInCooldown}
                  onMouseEnter={() => isInCooldown && setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className={`p-1.5 xs:p-2 rounded-full transition-all duration-200 ${
                    isInCooldown
                      ? 'text-green-500 bg-green-50 cursor-not-allowed'
                      : isSendingReminder
                      ? 'text-orange-400 bg-orange-50 opacity-50 cursor-not-allowed'
                      : 'text-orange-400 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  aria-label={
                    isInCooldown
                      ? t('actions.reminderSent')
                      : isSendingReminder
                      ? t('actions.sendingReminder')
                      : t('actions.sendReminder')
                  }
                >
                  {isInCooldown ? (
                    <CheckCircle className="w-4 h-4 xs:w-5 xs:h-5" />
                  ) : (
                    <Bell className={`w-4 h-4 xs:w-5 xs:h-5 ${isSendingReminder ? 'animate-pulse' : ''}`} />
                  )}
                </button>
                {isInCooldown && showTooltip && timeUntilNext && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap z-50 pointer-events-none">
                    {t('actions.reminderCooldown', { hours: timeUntilNext.hours, minutes: timeUntilNext.minutes })}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleTrashClick}
              className={`p-1.5 xs:p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200 ${
                isDraft && !hasContent ? 'invisible' : ''
              }`}
              aria-label={isPlanned ? t('schedule.edit') : t('actions.delete')}
            >
              <Trash2 className="w-4 h-4 xs:w-5 xs:h-5" />
            </button>
            {!isPlanned && (
              <ChevronRight className={`w-3.5 h-3.5 xs:w-4 xs:h-4 transition-all duration-200 ${
                isCurrent 
                  ? 'text-primary-600 transform translate-x-1' 
                  : 'text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1'
              }`} />
            )}
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden sm:flex sm:items-start sm:justify-between">
        <div className="flex items-start space-x-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
            isCurrent 
              ? isDraft
                ? 'bg-gradient-to-br from-primary-100 to-primary-50 ring-4 ring-primary-100/50 animate-pulse-subtle'
                : 'bg-primary-100 ring-4 ring-primary-50'
              : 'bg-gray-50 group-hover:bg-primary-50 group-hover:ring-4 group-hover:ring-primary-50/50'
          }`}>
            {isCurrent ? (
              isDraft ? (
                <Pen className="w-5 h-5 text-primary-600 transition-all duration-300 group-hover:scale-110" />
              ) : (
                <Check className="w-6 h-6 text-primary-600" />
              )
            ) : isScheduledInvoice ? (
              <Repeat2 className="w-6 h-6 text-gray-500 group-hover:text-primary-600 transition-colors duration-200" />
            ) : isFutureInvoice ? (
              <Clock className="w-6 h-6 text-gray-500 group-hover:text-primary-600 transition-colors duration-200" />
            ) : (
              <FileText className="w-6 h-6 text-gray-400 group-hover:text-primary-600 transition-colors duration-200" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-row items-center gap-3 mb-1">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {isDraft ? t('invoice.workingDraft') : t('invoice.invoiceFor', { company: invoice.recipient.company })}
                {isScheduledInvoice && (
                  <button
                    onClick={handleScheduleClick}
                    className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors duration-200"
                  >
                    <Repeat2 className="w-3 h-3" />
                    {t(`schedule.${scheduleType}`)}
                  </button>
                )}
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {!isPlanned && <StatusBadge status={invoice.status} />}
                {!isPlanned && (
                  <PaymentStatusSelector
                    status={invoice.paymentStatus}
                    onStatusChange={(status) => onUpdatePaymentStatus(status)}
                  />
                )}
              </div>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              {isPlanned ? (
                formattedNextInvoiceDate && (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1 text-primary-500" />
                    <span className="text-primary-600 font-medium">{formattedNextInvoiceDate}</span>
                  </div>
                )
              ) : (
                <>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                    {formattedDate}
                  </div>
                  {formattedNextInvoiceDate && (
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-primary-500" />
                      <span className="text-primary-600 font-medium">{formattedNextInvoiceDate}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center">
                <Building className="w-4 h-4 mr-1" />
                <span className="truncate max-w-none">{invoice.sender.company}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <div className="text-right">
            <p className="text-2xl font-medium text-gray-900">
              {formatCurrency(invoiceTotal)}
            </p>
            <div className="flex items-center justify-end gap-2 mt-2">
              {!isPlanned && (
                <p className={`text-sm font-medium ${isDraft ? 'text-primary-500' : 'text-gray-500'}`}>
                  {displayNumber}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            {!isPlanned && (
              <button
                onClick={onCopy}
                className={`p-2 text-gray-400 hover:text-primary-600 rounded-full hover:bg-primary-50 transition-colors duration-200 ${
                  isDraft ? 'invisible' : ''
                }`}
                aria-label={t('actions.copy')}
              >
                <Copy className="w-5 h-5" />
              </button>
            )}
            {!isPlanned && !isDraft && isOverdue && (
              <div className="relative group/reminder">
                <button
                  onClick={handleReminderClick}
                  disabled={isSendingReminder || isInCooldown}
                  onMouseEnter={() => isInCooldown && setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className={`p-2 rounded-full transition-all duration-200 ${
                    isInCooldown
                      ? 'text-green-500 bg-green-50 cursor-not-allowed'
                      : isSendingReminder
                      ? 'text-orange-400 bg-orange-50 opacity-50 cursor-not-allowed'
                      : 'text-orange-400 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  aria-label={
                    isInCooldown
                      ? t('actions.reminderSent')
                      : isSendingReminder
                      ? t('actions.sendingReminder')
                      : t('actions.sendReminder')
                  }
                >
                  {isInCooldown ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <Bell className={`w-5 h-5 ${isSendingReminder ? 'animate-pulse' : ''}`} />
                  )}
                </button>
                {isInCooldown && showTooltip && timeUntilNext && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap z-50 pointer-events-none">
                    {t('actions.reminderCooldown', { hours: timeUntilNext.hours, minutes: timeUntilNext.minutes })}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleTrashClick}
              className={`p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200 ${
                isDraft && !hasContent ? 'invisible' : ''
              }`}
              aria-label={isPlanned ? t('schedule.edit') : t('actions.delete')}
            >
              <Trash2 className="w-5 h-5" />
            </button>
            {!isPlanned && (
              <ChevronRight className={`w-5 h-5 transition-all duration-200 ${
                isCurrent 
                  ? 'text-primary-600 transform translate-x-1' 
                  : 'text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1'
              }`} />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return prevProps.invoice.id === nextProps.invoice.id &&
    prevProps.invoice.invoiceNumber === nextProps.invoice.invoiceNumber &&
    prevProps.invoice.status === nextProps.invoice.status &&
    prevProps.invoice.paymentStatus === nextProps.invoice.paymentStatus &&
    prevProps.invoice.lastReminderSentAt === nextProps.invoice.lastReminderSentAt &&
    prevProps.isCurrent === nextProps.isCurrent &&
    prevProps.isPlanned === nextProps.isPlanned;
}); 